import { clsx } from "clsx";
import type { ReactNode, SelectHTMLAttributes } from "react";
import { useId } from "react";

type SelectProps = {
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  children: ReactNode;
  className?: string;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, "className">;

export const Select = ({
  label,
  error,
  hint,
  placeholder,
  children,
  className,
  id,
  ...props
}: SelectProps) => {
  const generatedId = useId();
  const selectId = id || generatedId;

  return (
    <div className={clsx("w-full", className)}>
      {label && (
        <label htmlFor={selectId} className="mb-2 block font-semibold text-ocean-800 text-sm">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={selectId}
          className={clsx(
            "w-full rounded-xl border-[1.5px] bg-white/90 px-4 py-3.5 text-base text-ocean-900 transition-all duration-200",
            "focus:border-ocean-500 focus:shadow-focus focus:outline-none",
            error
              ? "border-error-border bg-error-bg/50 focus:border-error-border"
              : "border-ocean-200 hover:border-ocean-300",
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children}
        </select>
      </div>
      {error && <p className="mt-1.5 text-error-text text-sm">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-ocean-500 text-sm">{hint}</p>}
    </div>
  );
};
